import moment from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import GameNameEditable from '../Menus/Game/GameNameEditable';
import { QuickRouter } from '../TwoD/QuickRouter';

import '../Menus/Lobby/saved-games-modal.css';
import './saves-page.css';


/**
 * CC_FE_PAGES (2026-06-01).
 *
 * The `/saves` page — the user's resumable games. This is the body of
 * the retired `<SavedGamesModal>` promoted to a route-level page: same
 * data source (`listGames` + `list-update-event`), same row / rename /
 * Resume affordances. SavedGamesModal.js is left in place per
 * no-blind-deletes; it is simply no longer mounted.
 *
 * Resume navigates to `/game/:lobbyUuid` and calls `resumeGame(gameId)`.
 * CC_FE_PAGES_TIGHTEN (2026-06-01): the saved-games list payload now
 * carries the lobby uuid (`GameHotSerializer.lobby`), so Resume routes
 * by uuid — `/game/<lobby>` — while `resumeGame` still uses the integer
 * game id internally. A row whose `lobby` is missing or the defensive
 * `'ERROR MISSING LOBBY'` sentinel is non-resumable: its Resume button
 * is disabled (chose (a) per §3.4 — most explicit to the user that
 * something is wrong with that saved game).
 */
const ERROR_MISSING_LOBBY = 'ERROR MISSING LOBBY';


export default function SavesPage({ app }) {
    const [games, setGames] = useState([]);
    // Two-click confirm for Delete, mirroring EscapeMenu.handleAbandon
    // (CC_LOBBY_GAME_CRUD). Keyed by game id so confirm state on one row
    // doesn't bleed into another. Cleared after a successful abandon and
    // when the row drops out of the list on the next list-update.
    const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);

    // Source of truth for "who am I", mirroring SavedGamesModal /
    // GameDetailsComponent. Guarded because the authenticator may not be
    // hydrated yet.
    const currentUser =
        app && app.state && app.state.authenticator
        && app.state.authenticator.account
        && app.state.authenticator.account.user
            ? app.state.authenticator.account.user
            : null;
    const currentUsername = currentUser ? currentUser.username : null;
    const currentUserId = currentUser ? currentUser.id : null;

    useEffect(() => {
        const handler = (event) => {
            const data = (event && event.detail) || {};
            const mine = Array.isArray(data.games_list)
                ? data.games_list : [];
            const joined = Array.isArray(data.joined_games)
                ? data.joined_games : [];
            const combined = [...mine, ...joined];
            combined.sort((a, b) => {
                const aCreated = (a && a.game_data
                    && a.game_data.created) || '';
                const bCreated = (b && b.game_data
                    && b.game_data.created) || '';
                // Descending: most-recently-created first.
                if (aCreated < bCreated) return 1;
                if (aCreated > bCreated) return -1;
                return 0;
            });
            setGames(combined);
        };
        window.addEventListener('list-update-event', handler);
        if (app && app.game && app.game.gameApi) {
            app.game.gameApi.listGames();
        }
        return () => {
            window.removeEventListener('list-update-event', handler);
        };
    }, [app]);

    const onBack = useCallback(() => {
        // CC_FE_GAPS (2026-06-01): /saves had no way out but the URL bar.
        // One button, one route to the Main Menu.
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    }, []);

    const onDelete = useCallback((gameId) => {
        // Owner-only Abandon — backend (engine/controls/delete_game.py)
        // is the authority and refuses non-owner callers, but the row
        // only renders the button when isMine is true. Two-click
        // confirm matches EscapeMenu's pattern: first click arms, second
        // click commits. After commit, optimistically drop the row; the
        // server reconciles via the pushed List below.
        if (gameId == null) return;
        if (confirmingDeleteId !== gameId) {
            setConfirmingDeleteId(gameId);
            return;
        }
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.abandonGame(gameId);
        setGames((prev) => prev.filter((entry) => {
            const id = entry && entry.game_data && entry.game_data.id;
            return id !== gameId;
        }));
        setConfirmingDeleteId(null);
        // No explicit listGames() reconcile here: delete_game now pushes a
        // fresh List to the caller's channel as its final step, and the
        // mount effect's `list-update-event` listener applies it. Firing
        // our own listGames() used to race the Delete — Celery runs
        // `--concurrency=2 -P threads`, so the second 'List' task ran in
        // parallel with the Delete task and committed its read before the
        // game was deleted, re-adding the just-removed row
        // (CC_SAVES_DELETE_NO_REFRESH, 2026-06-03). The server-side push
        // reads after the delete in the same task, so it can't race.
    }, [app, confirmingDeleteId]);

    const onResume = useCallback((gameId, lobby) => {
        if (!app || !app.game || !app.game.gameApi) return;
        // Non-resumable rows are disabled in the UI; guard here too in
        // case the click slips through.
        if (!lobby || lobby === ERROR_MISSING_LOBBY) return;
        // resumeGame still keys on the integer game id internally; only
        // the URL navigation moved to the lobby uuid.
        app.game.gameApi.resumeGame(gameId);
        window.dispatchEvent(
            new CustomEvent('quick-router-event',
                { detail: '/game/' + lobby }));
    }, [app]);

    return (
        <div className="saves-page">
            <QuickRouter />
            <Button
                variant="honored"
                size="sm"
                className="saves-page-back"
                onClick={onBack}
            >
                Back
            </Button>
            <h2 className="saves-page-title">Saved Games</h2>
            {games.length === 0 ? (
                <p className="saved-games-empty">
                    No saved games. Start a game and use
                    Leave Game to keep one for later.
                </p>
            ) : (
                <ul className="saved-games-list">
                    {games.map((entry) => {
                        const gameData =
                            (entry && entry.game_data) || {};
                        const players =
                            (entry && entry.game_players_data) || [];
                        const gameId = gameData.id;
                        const lobby = gameData.lobby;
                        const canResume = !!lobby
                            && lobby !== ERROR_MISSING_LOBBY;
                        const status = gameData.status || '';
                        const createdBy = gameData.created_by || '';
                        const createdById = gameData.created_by_id;
                        const isMine = currentUserId != null
                            && createdById === currentUserId;
                        const activePlayer =
                            gameData.active_player_name;
                        const createdRelative = gameData.created
                            ? moment(gameData.created).fromNow()
                            : '';
                        return (
                            <li key={gameId}
                                className="saved-games-row">
                                <div className="saved-games-main">
                                    <div className="saved-games-titleline">
                                        <span className="saved-games-name">
                                            <GameNameEditable
                                                gameId={gameId}
                                                name={gameData.name}
                                                createdById={createdById}
                                                currentUserId={currentUserId}
                                                onRename={(newName) => {
                                                    if (app && app.game
                                                        && app.game.gameApi) {
                                                        app.game.gameApi
                                                            .nameGame(gameId, newName);
                                                    }
                                                }}
                                            />
                                        </span>
                                        <span className={isMine
                                            ? 'saved-games-chip saved-games-chip-yours'
                                            : 'saved-games-chip saved-games-chip-joined'}>
                                            {isMine ? 'Yours' : 'Joined'}
                                        </span>
                                    </div>
                                    <div className="saved-games-meta">
                                        <span className="saved-games-status">
                                            {status}
                                        </span>
                                        {createdRelative && (
                                            <span className="saved-games-created">
                                                {createdRelative}
                                            </span>
                                        )}
                                        {createdBy && (
                                            <span className="saved-games-createdby">
                                                by {createdBy}
                                            </span>
                                        )}
                                    </div>
                                    {activePlayer && (
                                        <div className="saved-games-active">
                                            Waiting on: {activePlayer}
                                        </div>
                                    )}
                                    {players.length > 0 && (
                                        <ul className="saved-games-roster">
                                            {players.map((seat) => {
                                                const seatId = seat
                                                    && seat.id;
                                                const isYou =
                                                    currentUsername
                                                    && seat
                                                    && seat.username
                                                        === currentUsername;
                                                const liveColor =
                                                    seat && seat.is_live
                                                        ? 'green'
                                                        : 'brown';
                                                return (
                                                    <li key={seatId}
                                                        className={isYou
                                                            ? 'saved-games-seat saved-games-seat-you'
                                                            : 'saved-games-seat'}>
                                                        <span
                                                            className="saved-games-seat-dot"
                                                            style={{ color: liveColor }}
                                                        >
                                                            &#9679;
                                                        </span>
                                                        <span className="saved-games-seat-name">
                                                            {seat
                                                                && seat.display_name}
                                                            {isYou
                                                                ? ' (you)'
                                                                : ''}
                                                        </span>
                                                        <span className="saved-games-seat-status">
                                                            {seat
                                                                && seat.status}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                                <Button
                                    variant="seed"
                                    size="sm"
                                    className="saved-games-resume"
                                    disabled={!canResume}
                                    onClick={() => onResume(gameId, lobby)}
                                >
                                    Resume
                                </Button>
                                {isMine && (
                                    <Button
                                        variant={
                                            confirmingDeleteId === gameId
                                                ? 'dread'
                                                : 'outline-dread'}
                                        size="sm"
                                        className="saved-games-delete"
                                        onClick={() => onDelete(gameId)}
                                    >
                                        {confirmingDeleteId === gameId
                                            ? 'Confirm Delete'
                                            : 'Delete'}
                                    </Button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
